import {
  DEFAULT_RECRUITING_INVITATION_PAGE,
  DEFAULT_RECRUITING_INVITATION_PAGE_SIZE,
  DEFAULT_RECRUITING_INVITATION_SORT,
  DEFAULT_RECRUITING_INVITATION_SORT_ORDER,
  INVITATION_STATUS_FILTER_ALL,
  RECRUITING_INVITATION_SORT_FIELDS,
  type InvitationStatusFilterValue,
  type RecruitingInvitationSortField,
  type RecruitingInvitationSortOrder,
} from '../constants'
import { RecruitingInvitationStatusEnum } from '../schema/recruiting.schema'
import { getCurrentYearRange } from 'helpers/datetime'
import {
  InvitationDateFilters,
  InvitationListFilters,
  ResolvedInvitationDateFilters,
  ResolvedInvitationPaginationParams,
  ResolvedInvitationSortParams,
} from '../types'

/** Jan 1 through Dec 31 of the current calendar year. */
export function getDefaultInvitationDateRange(): ResolvedInvitationDateFilters {
  const { start, end } = getCurrentYearRange()
  return {
    benefitEffectiveStartDate: start,
    benefitEffectiveEndDate: end,
  }
}

export function resolveInvitationDateFilters(
  filters: InvitationDateFilters
): ResolvedInvitationDateFilters {
  if (filters.benefitEffectiveStartDate && filters.benefitEffectiveEndDate) {
    return {
      benefitEffectiveStartDate: filters.benefitEffectiveStartDate,
      benefitEffectiveEndDate: filters.benefitEffectiveEndDate,
    }
  }
  /** Applies the current calendar year when URL date params are missing. */
  return getDefaultInvitationDateRange()
}

export function resolveInvitationPaginationParams(
  filters: Pick<InvitationListFilters, 'page' | 'pageSize'>
): ResolvedInvitationPaginationParams {
  const pageRaw = Number(filters.page)
  const pageSizeRaw = Number(filters.pageSize)

  const page =
    Number.isFinite(pageRaw) && pageRaw >= 1
      ? Math.floor(pageRaw)
      : DEFAULT_RECRUITING_INVITATION_PAGE

  const pageSize =
    Number.isFinite(pageSizeRaw) && pageSizeRaw >= 1
      ? Math.floor(pageSizeRaw)
      : DEFAULT_RECRUITING_INVITATION_PAGE_SIZE

  return { page, pageSize }
}

export function resolveInvitationSortParams(
  filters: InvitationListFilters
): ResolvedInvitationSortParams {
  const sortField = filters.sort as RecruitingInvitationSortField
  const sort = RECRUITING_INVITATION_SORT_FIELDS.includes(sortField)
    ? sortField
    : DEFAULT_RECRUITING_INVITATION_SORT

  let order: RecruitingInvitationSortOrder =
    DEFAULT_RECRUITING_INVITATION_SORT_ORDER
  if (filters.order === 'ASC') {
    order = 'ASC'
  } else if (filters.order === 'DESC') {
    order = 'DESC'
  }

  return { sort, order }
}

const STATUS_FILTER_VALUES = new Set<string>([
  RecruitingInvitationStatusEnum.Active,
  RecruitingInvitationStatusEnum.Expired,
  RecruitingInvitationStatusEnum.Cancelled,
])

/** `null` means show all statuses. */
export function resolveInvitationStatusFilter(
  filters: Pick<InvitationListFilters, 'status'>
): RecruitingInvitationStatusEnum | null {
  const status = filters.status
  if (!status || status === INVITATION_STATUS_FILTER_ALL) {
    return null
  }
  if (STATUS_FILTER_VALUES.has(status)) {
    return status as RecruitingInvitationStatusEnum
  }
  return null
}

export function toInvitationStatusFilterParam(
  value: InvitationStatusFilterValue
): string {
  if (value === INVITATION_STATUS_FILTER_ALL) {
    return INVITATION_STATUS_FILTER_ALL
  }
  return value
}

/** Omits `status` when the filter is All (default). */
export function resolveInvitationStatusParams(
  filters: Pick<InvitationListFilters, 'status'>
) {
  const status = resolveInvitationStatusFilter(filters)
  if (status == null) {
    return {}
  }
  return { status }
}
